import { apiRequest } from "@/lib/queryClient";

/**
 * Uploads a file to object storage through a validated upload reservation
 * and returns the served URL to store as a media record's fileUrl.
 *
 * 1. Request a short-lived upload URL from the API (JSON metadata only).
 * 2. PUT the exact file bytes to that authenticated, size/type-bound URL.
 * 3. Return `/api/storage/objects/...` — the app-served URL for the object.
 *
 * This keeps large files (e.g. SOP walkthrough videos) out of the database;
 * previously they were stored inline as base64 data URLs.
 */
export async function uploadFileToObjectStorage(file: File): Promise<string> {
  const contentType = file.type || "application/octet-stream";

  const res = await apiRequest("POST", "/api/storage/uploads/request-url", {
    name: file.name,
    // The upload endpoint requires size >= 1; empty files have nothing to store.
    size: Math.max(file.size, 1),
    contentType,
  });
  const { uploadURL, objectPath } = (await res.json()) as {
    uploadURL: string;
    objectPath: string;
  };

  // A direct PUT can otherwise remain pending indefinitely when a connection
  // stalls. Two minutes still leaves ample time for normal uploads (including
  // the 50MB maximum accepted by the UI).
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2 * 60 * 1000);
  let putRes: Response;
  try {
    putRes = await fetch(uploadURL, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error(`Upload of "${file.name}" timed out after 2 minutes`);
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
  if (!putRes.ok) {
    throw new Error(`Failed to upload "${file.name}" to storage (${putRes.status})`);
  }

  // objectPath already includes the /objects/ prefix, e.g. /objects/uploads/<uuid>
  return `/api/storage${objectPath}`;
}
